<script setup lang="ts">
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogClose,
        DialogFooter,
        DialogDescription,
        DialogBody,
    } from '@/components/ui/dialog';
    import { computed, watch } from 'vue';
    import { toast } from 'vue-sonner';
    import { useForm, usePage } from '@inertiajs/vue3';
    import { useI18n } from 'vue-i18n';
    import { useQueryClient } from '@tanstack/vue-query';
    import { useVoucherStore } from '@/stores/voucher';
    import QueryKey from '@/enums/QueryKey';
    import type { SharedData, Voucher } from '@/types';
    import type { Table } from '@tanstack/vue-table';
    import VoucherAssignAssignedForm from '@/components/vouchers/VoucherAssignAssignedForm.vue';

    const props = defineProps<{
        table: Table<Voucher>;
    }>();

    const voucherStore = useVoucherStore();
    const page = usePage<SharedData>();
    const companyId = computed(() => page.props.auth.user.selected_company_id);

    const { t } = useI18n();
    const queryClient = useQueryClient();

    const form = useForm({
        ids: [] as number[],
        assignee_ids: [] as string[],
        employee_ids: [] as number[],
        user_ids: [] as number[],
    });

    const handleUpload = () => {
        form.post(route('v2.voucher.admin.assign.selection'), {
            preserveScroll: true,
            onSuccess: () => {
                form.resetAndClearErrors();
                toast.success(t('vouchers.select.assign_confirm.success'));
                props.table.resetRowSelection();
                queryClient.invalidateQueries({ queryKey: [QueryKey.VOUCHERS, companyId] });
                voucherStore.toggleOpen('assigned', false);
            },
            onError: (error: any) => {
                if (error instanceof Error) {
                    toast.error(t('vouchers.select.assign_confirm.error'));
                }
            },
        });
    };

    watch(
        () => voucherStore.validVoucherIds,
        (newIds) => {
            form.ids = newIds;
        },
        { immediate: true },
    );
</script>

<template>
    <Dialog
        :open="voucherStore.assignedModalOpen"
        @update:open="(value) => voucherStore.toggleOpen('assigned', value)"
    >
        <DialogContent class="sm:max-w-lg">
            <form
                @submit.prevent="handleUpload"
                class="flex max-h-full flex-col overflow-hidden"
            >
                <DialogHeader>
                    <DialogTitle tabindex="0">
                        {{ t('vouchers.select.assign_confirm.title') }}
                    </DialogTitle>
                </DialogHeader>

                <DialogBody class="space-y-5">
                    <DialogDescription class="flex flex-col gap-2">
                        {{
                            t('vouchers.select.assign_confirm.content', {
                                amount: voucherStore.validVoucherIds.length,
                            })
                        }}
                    </DialogDescription>

                    <VoucherAssignAssignedForm
                        :form="form"
                        :company-id="companyId"
                    />
                </DialogBody>

                <DialogFooter class="gap-2 border-t sm:justify-center">
                    <DialogClose as-child>
                        <Button
                            variant="outline"
                            size="sm"
                            @click="form.resetAndClearErrors()"
                        >
                            {{ t('forms.cancel') }}
                        </Button>
                    </DialogClose>

                    <Button
                        :disabled="form.processing"
                        :loading="form.processing"
                        size="sm"
                    >
                        {{ t('forms.save') }}
                    </Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>
</template>
